import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { CONSENT_DISPLAYED_TEXT_ITEMS } from '../../../services/consent-hub-service/constants';
import { i18n } from '../messages';
var boxStylesGrey = xcss({
  padding: 'space.200',
  backgroundColor: 'elevation.surface.sunken',
  borderStyle: 'solid',
  borderWidth: 'border.width',
  borderColor: 'color.border',
  overflowY: 'hidden'
});
var boxStylesBlue = xcss({
  padding: 'space.200',
  backgroundColor: 'color.background.accent.blue.subtlest',
  borderStyle: 'solid',
  borderWidth: 'border.width',
  borderColor: 'color.border',
  overflowY: 'hidden'
});
var inlineWrapperStyles = xcss(_defineProperty({
  // Small viewport defaults
  gap: 'space.200',
  alignItems: 'flex-start',
  flexDirection: 'column'
}, media.above.sm, {
  gap: 'space.300',
  flexDirection: 'row',
  alignItems: 'center'
}));
var noticeContainerStyles = xcss({
  flexDirection: 'column'
});
var buttonContainerStyles = xcss(_defineProperty({
  // Applies to below sm size
  flexDirection: 'row-reverse',
  flexWrap: 'wrap-reverse',
  justifyContent: 'start'
}, media.above.sm, {
  marginLeft: 'auto',
  flexWrap: 'nowrap',
  flexDirection: 'row'
}));
var linkStyles = xcss({
  textDecoration: 'underline'
});
var noop = function noop() {};
var getDisplayedTextMap = function getDisplayedTextMap(intl) {
  // We'll use this mapping to send the `displayedText` for each checkbox when saving to ConsentHub
  var displayedTextMap = CONSENT_DISPLAYED_TEXT_ITEMS.reduce(function (translatedTextMap, category) {
    var linkToCookiesTrackingNotice = intl.formatMessage(i18n.cookiesTrackingNotice);
    var bannerDescription = intl.formatMessage(i18n.useCookiesDescription, {
      linkToCookiesTrackingNotice: linkToCookiesTrackingNotice
    });
    // Set this for all items (including items with checkboxes in the UI), we'll override as needed below
    // for things like strictlyNecessary/acceptAll
    translatedTextMap[category] = bannerDescription;
    return translatedTextMap;
  }, {});
  return displayedTextMap;
};
var updateDisplayedTextMap = function updateDisplayedTextMap(intl, displayTextMap, isOnlyNecessary) {
  var updatedDisplayedTextMap = _objectSpread({}, displayTextMap);

  // Use description as message to translate
  var onlyNecessaryContent = intl.formatMessage(i18n.onlyNecessaryBtn);
  var acceptAllContent = intl.formatMessage(i18n.acceptAllBtn);

  // For additional auditing content, append the description related to the button when pressed
  Object.keys(updatedDisplayedTextMap).forEach(function (key) {
    var preferenceCategory = key;
    var additionalContent = isOnlyNecessary ? onlyNecessaryContent : acceptAllContent;
    var textContent = updatedDisplayedTextMap[preferenceCategory];
    updatedDisplayedTextMap[preferenceCategory] = "".concat(additionalContent, " \u2014 ").concat(textContent);
  });
  return updatedDisplayedTextMap;
};
/**
 * Component containing the UI aspects of the consent banner.
 *
 * Conditional rendering, analytics, feature flag evaluation, and most other
 * logic are located in the parent component ConsentBanner
 */
export var PresentationalConsentBanner = function PresentationalConsentBanner(_ref) {
  var _ref$onClickOnlyNeces = _ref.onClickOnlyNecessary,
    onClickOnlyNecessary = _ref$onClickOnlyNeces === void 0 ? noop : _ref$onClickOnlyNeces,
    _ref$onClickAcceptAll = _ref.onClickAcceptAll,
    onClickAcceptAll = _ref$onClickAcceptAll === void 0 ? noop : _ref$onClickAcceptAll,
    _ref$onClickPreferenc = _ref.onClickPreferences,
    onClickPreferences = _ref$onClickPreferenc === void 0 ? noop : _ref$onClickPreferenc,
    _ref$onClickTrackingN = _ref.onClickTrackingNoticeLink,
    onClickTrackingNoticeLink = _ref$onClickTrackingN === void 0 ? noop : _ref$onClickTrackingN,
    _ref$minWidth = _ref.minWidth,
    minWidth = _ref$minWidth === void 0 ? 400 : _ref$minWidth,
    _ref$bannerExperiment = _ref.bannerExperimentOneEnabled,
    bannerExperimentOneEnabled = _ref$bannerExperiment === void 0 ? false : _ref$bannerExperiment;
  var intl = useIntl();
  var displayedTextMap = useMemo(function () {
    return getDisplayedTextMap(intl);
  }, [intl]);
  var handleOnlyNecessaryClick = useCallback(function () {
    var displayedTextOnlyNecessaryMap = updateDisplayedTextMap(intl, displayedTextMap, true);
    onClickOnlyNecessary(displayedTextOnlyNecessaryMap);
  }, [onClickOnlyNecessary, intl, displayedTextMap]);
  var handleAcceptAllClick = useCallback(function () {
    var displayedTextAcceptAllMap = updateDisplayedTextMap(intl, displayedTextMap);
    onClickAcceptAll(displayedTextAcceptAllMap);
  }, [onClickAcceptAll, intl, displayedTextMap]);
  return /*#__PURE__*/React.createElement(Box, {
    role: "dialog",
    "aria-labelledby": "cookiesTrackingNoticeLink",
    xcss: bannerExperimentOneEnabled ? boxStylesBlue : boxStylesGrey,
    style: {
      minWidth: "".concat(minWidth, "px")
    }
  }, /*#__PURE__*/React.createElement(Inline, {
    xcss: inlineWrapperStyles
  }, /*#__PURE__*/React.createElement(Inline, {
    xcss: noticeContainerStyles
  }, /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, i18n.useCookiesDescription, {
    tagName: "span",
    values: {
      linkToCookiesTrackingNotice: /*#__PURE__*/React.createElement(Box, {
        as: "a",
        xcss: linkStyles,
        id: "cookiesTrackingNoticeLink",
        href: "https://www.atlassian.com/legal/cookies",
        target: "_blank",
        onClick: onClickTrackingNoticeLink
      }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.cookiesTrackingNotice))
    }
  }))), /*#__PURE__*/React.createElement(Inline, {
    space: "space.100",
    xcss: buttonContainerStyles
  }, /*#__PURE__*/React.createElement(Button, {
    appearance: "subtle",
    onClick: onClickPreferences
  }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.preferencesBtn)), /*#__PURE__*/React.createElement(Button, {
    onClick: handleOnlyNecessaryClick
  }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.onlyNecessaryBtn)), /*#__PURE__*/React.createElement(Button, {
    onClick: handleAcceptAllClick
  }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.acceptAllBtn)))));
};